import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { PiggyBank, TrendingUp, AlertTriangle, Plus, X, Loader2, CheckCircle } from "lucide-react";

export default function Budget() {
  const [budget, setBudget] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIncreaseModal, setShowIncreaseModal] = useState(false);
  const [increaseForm, setIncreaseForm] = useState({ requested_amount: 0, justification: "", category: "total" });
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [bRes, rRes] = await Promise.all([
        axios.get(`${API}/budget`),
        axios.get(`${API}/budget/increase-requests`),
      ]);
      setBudget(bRes.data);
      setEditForm(bRes.data);
      setRequests(rRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveBudget = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/budget`, editForm);
      setBudget(data); setEditMode(false); setMsg("Presupuesto actualizado");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { alert("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleIncreaseRequest = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/budget/increase-request`, increaseForm);
      setShowIncreaseModal(false);
      setMsg("Solicitud de aumento enviada al administrador");
      setTimeout(() => setMsg(""), 4000);
      load();
    } catch (e) { alert("Error"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#007934]" /></div>;
  if (!budget) return null;

  const totalSpent = (budget.spent_licencias || 0) + (budget.spent_external || 0) + (budget.spent_internal || 0);
  const remaining = (budget.max_total || 460000) - totalSpent;
  const pctTotal = Math.round((totalSpent / (budget.max_total || 460000)) * 100);

  const categories = [
    { key: "licencias", label: "Licencias", icon: "L", allocated: budget.budget_licencias || 0, spent: budget.spent_licencias || 0, color: "#007934" },
    { key: "external", label: "Recursos Externos", icon: "E", allocated: budget.budget_external || 0, spent: budget.spent_external || 0, color: "#00ACC1" },
    { key: "internal", label: "Recursos Internos", icon: "I", allocated: budget.budget_internal || 0, spent: budget.spent_internal || 0, color: "#8BC34A", auto: budget.spent_internal_auto },
  ];

  return (
    <div className="space-y-5 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Control de Presupuesto</h1>
          <p className="text-sm text-gray-500 mt-0.5">Ejercicio 2026 · Máximo aprobado: {(budget.max_total || 460000).toLocaleString("es-ES")}€</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditMode(!editMode)} className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50" data-testid="edit-budget-btn">
            {editMode ? "Cancelar" : "Editar"}
          </button>
          <button onClick={() => setShowIncreaseModal(true)} className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] flex items-center gap-2" data-testid="increase-budget-btn">
            <Plus size={16} /> Solicitar aumento
          </button>
        </div>
      </div>

      {msg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="budget-success-msg"><CheckCircle size={16}/>{msg}</div>}

      {/* Total Budget Card */}
      <div className="bg-gradient-to-br from-[#007934] to-[#4CAF50] rounded-2xl p-6 text-white shadow-lg" data-testid="total-budget-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-green-100 text-sm font-medium">Presupuesto Total 2026</p>
            <p className="text-4xl font-bold font-['Outfit'] mt-1">{(budget.max_total || 460000).toLocaleString("es-ES")}€</p>
          </div>
          <div className="text-right">
            <p className="text-green-100 text-sm">Ejecutado</p>
            <p className="text-3xl font-bold font-['Outfit']">{pctTotal}%</p>
          </div>
        </div>
        <div className="h-3 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-1000" style={{ width: `${Math.min(pctTotal,100)}%` }} data-testid="total-progress" />
        </div>
        <div className="flex justify-between mt-3 text-sm">
          <span className="text-green-100">Gastado: <strong className="text-white">{totalSpent.toLocaleString("es-ES")}€</strong></span>
          <span className="text-green-100">Disponible: <strong className="text-white">{remaining.toLocaleString("es-ES")}€</strong></span>
        </div>
        {pctTotal > 80 && <div className="mt-3 flex items-center gap-2 text-yellow-100 text-xs bg-yellow-500/20 rounded-lg px-3 py-2"><AlertTriangle size={14}/> Atención: ejecución por encima del 80%</div>}
      </div>

      {/* N07 (roadmap-valoriza-jev-2026-10-11, Ola 3) · tramo sin categorizar */}
      {Math.abs(budget.budget_sin_categorizar || 0) > 0.5 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-center gap-2" data-testid="budget-sin-categorizar-banner">
          <AlertTriangle size={14} />
          <span>
            <b>{(budget.budget_sin_categorizar || 0).toLocaleString("es-ES")}€</b> del presupuesto máximo no están asignados a ninguna categoría (Licencias + Externos + Internos suman {(budget.budget_categorizado_total || 0).toLocaleString("es-ES")}€ de {(budget.max_total || 0).toLocaleString("es-ES")}€). Moneda: {budget.moneda || "EUR"}.
          </span>
        </div>
      )}

      {editMode && (
        <div className="bg-white rounded-xl border border-[#007934]/30 p-5 mb-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 bg-[#007934] rounded-full" />
            <span className="font-semibold text-gray-900 text-sm">Presupuesto Total Máximo</span>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={editForm.max_total || 460000}
              onChange={e => setEditForm({ ...editForm, max_total: parseFloat(e.target.value) })}
              className="w-48 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
              data-testid="edit-max-total-input"
            />
            <span className="text-sm text-gray-500">€ — Presupuesto máximo aprobado para el ejercicio</span>
          </div>
        </div>
      )}
      {/* Categories */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {categories.map(cat => {
          const pct = cat.allocated > 0 ? Math.round((cat.spent / cat.allocated) * 100) : 0;
          return (
            <div key={cat.key} className="bg-white rounded-xl border border-gray-100 p-5 card-hover" data-testid={`budget-cat-${cat.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ background: cat.color }}>
                    {cat.icon}
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">{cat.label}</span>
                </div>
                <span className="text-xs font-bold" style={{ color: cat.color }}>{pct}%</span>
              </div>
              {editMode ? (
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-gray-500">Asignado (€)</label>
                    <input type="number" value={editForm[`budget_${cat.key}`] || 0}
                      onChange={e => setEditForm({...editForm, [`budget_${cat.key}`]: parseFloat(e.target.value)})}
                      className="w-full border border-gray-200 rounded px-2 py-1 text-sm mt-0.5" data-testid={`edit-budget-${cat.key}`} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Ejecutado (€)</label>
                    <input type="number" value={editForm[`spent_${cat.key}`] || 0}
                      onChange={e => setEditForm({...editForm, [`spent_${cat.key}`]: parseFloat(e.target.value)})}
                      className="w-full border border-gray-200 rounded px-2 py-1 text-sm mt-0.5" data-testid={`edit-spent-${cat.key}`} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-2xl font-bold text-gray-900 font-['Outfit']">{cat.spent.toLocaleString("es-ES")}€</div>
                  <div className="text-xs text-gray-400 mb-2">
                    de {cat.allocated.toLocaleString("es-ES")}€ asignados
                    {cat.auto && <span className="ml-1.5 bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-medium">Auto</span>}
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct,100)}%`, background: cat.color }} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1.5">Disponible: {(cat.allocated - cat.spent).toLocaleString("es-ES")}€</div>
                  {cat.auto && cat.key === "internal" && budget.spent_internal_detail && (
                    <div className="mt-2 text-[10px] text-gray-400">
                      Calculado automáticamente · {budget.spent_internal_detail.working_days} días laborables · {budget.spent_internal_detail.active_assignments} asignaciones activas
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {editMode && (
        <div className="flex gap-2">
          <button onClick={handleSaveBudget} disabled={saving} className="bg-[#007934] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center gap-2" data-testid="save-budget-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Guardar cambios
          </button>
          <button onClick={() => setEditMode(false)} className="border border-gray-200 text-gray-700 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">Cancelar</button>
        </div>
      )}

      {/* Increase Requests */}
      {requests.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-base font-bold text-gray-900 font-['Outfit'] mb-4">Solicitudes de Aumento</h3>
          <div className="space-y-2">
            {requests.map(r => (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-50 hover:bg-gray-50">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${r.status === "approved" ? "bg-green-500" : r.status === "pending" ? "bg-yellow-500" : "bg-gray-300"}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">+{r.requested_amount?.toLocaleString("es-ES")}€</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{r.status}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{r.justification}</p>
                  <p className="text-xs text-gray-400">{r.requested_by} · {new Date(r.created_at).toLocaleDateString("es-ES")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Increase Modal */}
      {showIncreaseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-fade-up" data-testid="increase-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">Solicitar Aumento de Presupuesto</h2>
              <button onClick={() => setShowIncreaseModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleIncreaseRequest} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Importe solicitado (€) *</label>
                <input type="number" required min="1" value={increaseForm.requested_amount}
                  onChange={e => setIncreaseForm({...increaseForm, requested_amount: parseFloat(e.target.value)})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="increase-amount-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Categoría</label>
                <select value={increaseForm.category} onChange={e => setIncreaseForm({...increaseForm, category: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  <option value="total">Total</option>
                  <option value="licencias">Licencias</option>
                  <option value="external">Recursos Externos</option>
                  <option value="internal">Recursos Internos</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Justificación *</label>
                <textarea required rows={4} value={increaseForm.justification}
                  onChange={e => setIncreaseForm({...increaseForm, justification: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
                  placeholder="Explica el motivo del aumento solicitado..." data-testid="increase-justification-input" />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowIncreaseModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="submit-increase-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null} Solicitar aumento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
